import React from 'react';
import { X, MapPin, Calendar, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { formatCurrency, formatPercentage } from '../utils/formatters';

const LOAN_TYPE_LABELS = {
  construction: 'Construction',
  bridge_refinance: 'Bridge (Refinance)',
  bridge_purchase: 'Bridge (Purchase)',
  portfolio: 'Portfolio'
};

const CompareView = ({ scenarios, onClose }) => {
  if (!scenarios || scenarios.length < 2) return null;

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Define comparison metrics based on loan type
  const getComparisonMetrics = (scenario) => {
    const { loan_type, metrics, inputs } = scenario;
    
    if (loan_type === 'construction') {
      return [
        { label: 'Loan Amount', value: formatCurrency(metrics.finalLoanAmount || metrics.final_loan_amount || 0) },
        { label: 'Total Project Cost', value: formatCurrency(metrics.totalProjectCost || metrics.total_project_cost || 0) },
        { label: 'Resulting LTV', value: formatPercentage(metrics.resultingLtv || metrics.resulting_ltv || 0) },
        { label: 'Closing Costs', value: formatCurrency(metrics.closingCosts || metrics.closing_costs || 0) },
        { label: 'Interest Reserve', value: formatCurrency(metrics.interestReserve || metrics.interest_reserve || 0) },
        { label: 'Equity Requirement', value: formatCurrency(metrics.equityRequirement || metrics.equity_requirement || 0) },
        { label: 'Interest Rate', value: `${inputs.interestRate || inputs.interest_rate || 0}%` },
      ];
    } else if (loan_type === 'bridge_refinance' || loan_type === 'bridge_purchase') {
      return [
        { label: 'Loan Amount', value: formatCurrency(metrics.loanAmount || 0) },
        { label: 'As-Is Value', value: formatCurrency(metrics.asIsValue || inputs.asIsValue || 0) },
        { label: 'LTV', value: formatPercentage(metrics.ltv || 0) },
        { label: 'Monthly Interest', value: formatCurrency(metrics.monthlyInterest || 0) },
        { label: 'Total Closing Costs', value: formatCurrency(metrics.totalClosingCosts || 0) },
        { label: 'Cash to Close', value: formatCurrency(metrics.cashToClose || 0) },
        { label: 'Interest Rate', value: `${inputs.interestRate || 0}%` },
      ];
    } else if (loan_type === 'portfolio') {
      return [
        { label: 'Loan Amount', value: formatCurrency(metrics.loanAmount || 0) },
        { label: 'Purchase Price', value: formatCurrency(metrics.purchasePrice || inputs.purchasePrice || 0) },
        { label: 'LTV', value: formatPercentage(metrics.ltv || inputs.targetLTV || 0) },
        { label: 'IO Payment', value: formatCurrency(metrics.ioPayment || 0) },
        { label: 'P&I Payment', value: formatCurrency(metrics.piPayment || 0) },
        { label: 'Total Closing Costs', value: formatCurrency(metrics.totalClosingCosts || 0) },
        { label: 'Cash to Close', value: formatCurrency(metrics.cashToClose || 0) },
        { label: 'Interest Rate', value: `${inputs.interestRate || 0}%` },
      ];
    }
    return [];
  };

  // Get colors for columns
  const columnColors = ['bg-blue-50', 'bg-emerald-50', 'bg-amber-50'];
  const headerColors = ['bg-blue-100', 'bg-emerald-100', 'bg-amber-100'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-[#002F87]">Compare Scenarios</h2>
            <p className="text-sm text-slate-500">Side-by-side comparison of {scenarios.length} scenarios</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            data-testid="btn-close-compare"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Comparison Table */}
        <div className="flex-1 overflow-auto p-4">
          <div className="grid gap-4" style={{ gridTemplateColumns: `200px repeat(${scenarios.length}, 1fr)` }}>
            {/* Header Row */}
            <div className="font-semibold text-slate-700 p-3">Scenario Details</div>
            {scenarios.map((scenario, idx) => (
              <div key={scenario.id} className={`p-3 rounded-t-lg ${headerColors[idx]}`}>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  <span className="font-semibold text-slate-900 truncate text-sm">
                    {scenario.property_address || 'No address'}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-600">
                  <span className="px-2 py-0.5 bg-white/50 rounded font-medium">
                    {LOAN_TYPE_LABELS[scenario.loan_type] || scenario.loan_type}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(scenario.created_at)}
                  </span>
                </div>
              </div>
            ))}

            {/* Metrics Rows */}
            {scenarios[0] && getComparisonMetrics(scenarios[0]).map((_, metricIdx) => (
              <React.Fragment key={metricIdx}>
                <div className="p-3 text-sm font-medium text-slate-600 border-b border-slate-100">
                  {getComparisonMetrics(scenarios[0])[metricIdx]?.label}
                </div>
                {scenarios.map((scenario, idx) => {
                  const metrics = getComparisonMetrics(scenario);
                  return (
                    <div 
                      key={scenario.id} 
                      className={`p-3 text-sm font-mono border-b border-slate-100 ${columnColors[idx]}`}
                    >
                      {metrics[metricIdx]?.value || '-'}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <div className="flex justify-end">
            <Button
              onClick={onClose}
              variant="outline"
              className="mr-2"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareView;
